<script setup lang="ts">
/**
 * Contract Intelligence dashboard — pilot scope per
 * `data/Projekt Charter_Contract Intelligence Agent.docx` §3:
 * "Simpelt dashboard med overblik over leverancer og kontraktudløb."
 *
 * Reads the aggregated payload from `/api/contracts/dashboard` (one
 * server-side scan of the contracts-scoped chunks, deduped by source,
 * then projected into per-customer / per-billable / expiry-timeline /
 * upcoming-renewals shapes — see backend's `_build_contracts_dashboard`).
 *
 * No PrimeVue charts at this layer — pure HTML tables + a CSS-driven
 * horizontal bar for the upcoming-renewal timeline. Keeps the bundle
 * small and dependency-free; promote to a charting lib only when the
 * stakeholder asks for a specific visualization.
 */
import { computed, onMounted, ref } from "vue";

import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{ (e: "exit"): void }>();

type ContractRow = {
  source: string;
  title: string;
  url: string;
  contract_summary: string;
  contract_customer: string;
  contract_counterparty?: string;
  contract_we_are_buyer?: boolean | null;
  contract_parties: string[];
  contract_billable: string;
  contract_deliverables: string[];
  contract_start_date: string | null;
  contract_end_date: string | null;
  contract_renewal_date: string | null;
  contract_renewal_type: string;
  contract_termination_notice_days: number;
  contract_value_amount: number;
  contract_value_currency: string;
  contract_payment_terms_days: number;
};

type CounterpartyRow = {
  counterparty: string;
  count: number;
  total_value: number;
  currencies: string[];
};

type RenewalRow = {
  source: string;
  title: string;
  counterparty: string;
  renewal_date: string;
  termination_notice_days: number;
};

type ExpiryRow = {
  source: string;
  title: string;
  counterparty: string;
  end_date: string;
};

type Dashboard = {
  generated_at: string;
  contract_count: number;
  contracts: ContractRow[];
  by_counterparty: CounterpartyRow[];
  by_direction: { key: string; count: number }[];
  by_billable: { key: string; count: number }[];
  by_renewal_type: { key: string; count: number }[];
  expiry_timeline: ExpiryRow[];
  upcoming_renewals: RenewalRow[];
  total_value_by_currency: Record<string, number>;
};

/** Resolve the counterparty name for a row, preferring the new
 * `contract_counterparty` field but falling back to the legacy
 * `contract_customer` for index data that predates task #293. */
function counterpartyOf(c: ContractRow): string {
  return c.contract_counterparty || c.contract_customer || "(unknown)";
}

/** Direction badge: "we buy" / "we sell" / "?" — feeds the table's
 * direction column. Task #293 — clears up the "is Danoffice the
 * buyer or the seller here?" confusion. */
function directionOf(c: ContractRow): { label: string; klass: string } {
  if (c.contract_we_are_buyer === true) {
    return { label: "We buy", klass: "dir--buy" };
  }
  if (c.contract_we_are_buyer === false) {
    return { label: "We sell", klass: "dir--sell" };
  }
  return { label: "?", klass: "dir--unknown" };
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

const data = ref<Dashboard | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (USE_REAL_AUTH) {
      const t = await acquireToken();
      if (t) headers["Authorization"] = `Bearer ${t.token}`;
    } else if (props.devUser) {
      headers["X-Dev-User"] = props.devUser;
    }
    const r = await fetch(`${API_BASE}/api/contracts/dashboard`, {
      headers,
      cache: "no-store",
    });
    if (!r.ok) {
      const body = await r.text();
      throw new Error(`${r.status} — ${body.slice(0, 200)}`);
    }
    data.value = (await r.json()) as Dashboard;
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);

const generatedAt = computed(() => {
  if (!data.value?.generated_at) return "";
  return new Date(data.value.generated_at).toLocaleString();
});

function formatMoney(amount: number, currency: string): string {
  if (!amount) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`.trim();
  }
}

function daysUntil(dateStr: string): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr).getTime();
  if (isNaN(d)) return null;
  return Math.floor((d - Date.now()) / (1000 * 60 * 60 * 24));
}

function urgencyClass(days: number | null): string {
  if (days === null) return "";
  if (days < 0) return "row--expired";
  if (days < 30) return "row--urgent";
  if (days < 90) return "row--soon";
  return "";
}
</script>

<template>
  <main class="dash">
    <header class="dash__header">
      <button type="button" class="dash__back" @click="emit('exit')">
        ← Hub
      </button>
      <div class="dash__title">
        <h1>Contract Intelligence</h1>
        <p class="dash__sub">
          Pilot overview — 5–7 DOIT contracts ingested from Risma
          <span v-if="generatedAt" class="dash__stamp">
            · refreshed {{ generatedAt }}
          </span>
        </p>
      </div>
      <button
        type="button"
        class="dash__refresh"
        :disabled="loading"
        @click="load"
      >
        {{ loading ? "loading…" : "Refresh" }}
      </button>
    </header>

    <div v-if="error" class="dash__error">
      <strong>Failed to load dashboard:</strong> {{ error }}
    </div>

    <div v-if="loading && !data" class="dash__loading">Loading contracts…</div>

    <section v-if="data" class="dash__grid">
      <!-- Headline KPIs -->
      <article class="card card--kpi">
        <div class="kpi__big">{{ data.contract_count }}</div>
        <div class="kpi__label">Contracts in pilot</div>
      </article>

      <article class="card card--kpi">
        <div class="kpi__big">{{ data.upcoming_renewals.length }}</div>
        <div class="kpi__label">Renewals in next 90 days</div>
      </article>

      <article class="card card--kpi">
        <div class="kpi__values">
          <div
            v-for="(amount, ccy) in data.total_value_by_currency"
            :key="ccy"
            class="kpi__money"
          >
            {{ formatMoney(amount, ccy) }}
          </div>
          <div
            v-if="!Object.keys(data.total_value_by_currency).length"
            class="kpi__money kpi__money--empty"
          >
            —
          </div>
        </div>
        <div class="kpi__label">Total contract value</div>
      </article>

      <!-- Upcoming renewals -->
      <article class="card card--wide">
        <h2>Upcoming renewals (90 days)</h2>
        <p v-if="!data.upcoming_renewals.length" class="card__empty">
          No contracts coming up for renewal in the next 90 days.
        </p>
        <table v-else class="dash-table">
          <thead>
            <tr>
              <th>Renewal date</th>
              <th>Days</th>
              <th>Customer</th>
              <th>Contract</th>
              <th>Notice (days)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in data.upcoming_renewals"
              :key="r.source"
              :class="urgencyClass(daysUntil(r.renewal_date))"
            >
              <td>{{ r.renewal_date }}</td>
              <td>{{ daysUntil(r.renewal_date) }}</td>
              <td>{{ r.counterparty }}</td>
              <td>{{ r.title }}</td>
              <td>{{ r.termination_notice_days || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- By counterparty -->
      <article class="card">
        <h2>By counterparty</h2>
        <p class="card-hint">
          The non-Danoffice party — whichever side of the contract that is. See
          "Direction" in the table below for who pays whom.
        </p>
        <table class="dash-table">
          <thead>
            <tr>
              <th>Counterparty</th>
              <th>#</th>
              <th>Total value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in data.by_counterparty" :key="c.counterparty">
              <td>{{ c.counterparty }}</td>
              <td>{{ c.count }}</td>
              <td>
                <span v-for="ccy in c.currencies" :key="ccy" class="ccy">
                  {{ formatMoney(c.total_value, ccy) }}
                </span>
                <span v-if="!c.currencies.length">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- Direction (we buy / we sell) -->
      <article class="card">
        <h2>Direction</h2>
        <p class="card-hint">
          Whether Danoffice is the buyer or the seller in each contract.
        </p>
        <table class="dash-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>#</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in data.by_direction" :key="d.key">
              <td>{{ d.key }}</td>
              <td>{{ d.count }}</td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- By billable -->
      <article class="card">
        <h2>Billability</h2>
        <table class="dash-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>#</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in data.by_billable" :key="b.key">
              <td>{{ b.key }}</td>
              <td>{{ b.count }}</td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- By renewal type -->
      <article class="card">
        <h2>Renewal type</h2>
        <table class="dash-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>#</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in data.by_renewal_type" :key="r.key">
              <td>{{ r.key }}</td>
              <td>{{ r.count }}</td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- All contracts -->
      <article class="card card--wide">
        <h2>All pilot contracts</h2>
        <table class="dash-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Counterparty</th>
              <th>Direction</th>
              <th>End date</th>
              <th>Billable</th>
              <th>Renewal</th>
              <th>Value</th>
              <th>Net days</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in data.contracts" :key="c.source">
              <td>{{ c.title }}</td>
              <td>{{ counterpartyOf(c) }}</td>
              <td>
                <span class="dir" :class="directionOf(c).klass">
                  {{ directionOf(c).label }}
                </span>
              </td>
              <td>{{ c.contract_end_date?.slice(0, 10) || "—" }}</td>
              <td>{{ c.contract_billable || "—" }}</td>
              <td>{{ c.contract_renewal_type || "—" }}</td>
              <td>
                {{
                  c.contract_value_amount
                    ? formatMoney(
                        c.contract_value_amount,
                        c.contract_value_currency,
                      )
                    : "—"
                }}
              </td>
              <td>{{ c.contract_payment_terms_days || "—" }}</td>
              <td>
                <a v-if="c.url" :href="c.url" target="_blank" rel="noreferrer">
                  open ↗
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </article>
    </section>
  </main>
</template>

<style scoped>
.dash {
  padding: var(--space-4);
  max-width: 1400px;
  margin: 0 auto;
}
.dash__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}
.dash__back {
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-1) var(--space-3);
  cursor: pointer;
}
.dash__title {
  flex: 1;
}
.dash__title h1 {
  margin: 0;
  font-size: 1.6rem;
}
.dash__sub {
  color: var(--color-muted);
  margin: 0;
  font-size: 0.9rem;
}
.dash__stamp {
  font-size: 0.8rem;
}
.dash__refresh {
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-md);
  cursor: pointer;
}
.dash__error,
.dash__loading {
  margin: var(--space-4) 0;
  padding: var(--space-3);
  border-radius: var(--radius-md);
}
.dash__error {
  background: var(--color-surface);
  border: 1px solid var(--color-error);
  color: var(--color-error);
}
.dash__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-3);
}
.card-hint {
  color: var(--color-muted);
  font-size: 0.8rem;
  margin: 0 0 var(--space-2);
  font-style: italic;
}
.dir {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}
.dir--buy {
  background: rgba(217, 119, 6, 0.15);
  color: #b25800;
}
.dir--sell {
  background: rgba(5, 122, 85, 0.15);
  color: #047857;
}
.dir--unknown {
  background: rgba(127, 127, 127, 0.12);
  color: var(--color-muted);
}
.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  overflow: auto;
}
.card--wide {
  grid-column: span 3;
}
.card h2 {
  margin: 0 0 var(--space-2) 0;
  font-size: 1.05rem;
}
.card__empty {
  color: var(--color-muted);
  font-style: italic;
  margin: 0;
}
.card--kpi {
  text-align: center;
}
.kpi__big {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--space-1);
}
.kpi__label {
  color: var(--color-muted);
  font-size: 0.85rem;
}
.kpi__values {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-1);
}
.kpi__money {
  font-size: 1.3rem;
  font-weight: 600;
}
.kpi__money--empty {
  color: var(--color-muted);
}
.dash-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.dash-table th,
.dash-table td {
  text-align: left;
  padding: var(--space-1) var(--space-2);
  border-bottom: 1px solid var(--color-border);
}
.dash-table th {
  font-weight: 600;
  color: var(--color-muted);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.row--expired {
  background: rgba(192, 0, 0, 0.08);
}
.row--urgent {
  background: rgba(220, 100, 0, 0.08);
}
.row--soon {
  background: rgba(220, 180, 0, 0.05);
}
.ccy {
  display: inline-block;
  margin-right: var(--space-2);
}
@media (max-width: 900px) {
  .dash__grid {
    grid-template-columns: 1fr;
  }
  .card--wide {
    grid-column: span 1;
  }
}
</style>
